/**
 * @element PersonAvatar
 * @description Someone's picture, with whatever this surface draws when
 *   there isn't one.
 *
 *   Owns the two things every avatar gets wrong and nothing else:
 *
 *     1. THE PICTURE. One `<img>`, sized and cropped the same way
 *        everywhere (`object-cover` on a circle, so a non-square upload is
 *        centred rather than squashed).
 *
 *     2. THE FAILURE. A photo URL that 404s — a deleted upload, an OAuth
 *        link that expired — renders a broken-image glyph or an empty
 *        frame, which is worse than the monogram it replaced. So a load
 *        error falls back to the same `fallback` used when no picture
 *        exists, and the slot never becomes a hole.
 *
 *   It deliberately does NOT dictate the fallback. The header dropdown
 *   draws two initials on a hashed tint; the Messages page draws one on a
 *   solid brand fill. Those are surface decisions, and folding them
 *   together would be a redesign wearing a refactor's clothes. Pass the
 *   fallback you already draw.
 *
 *   Decorative by default: these sit beside the person's name in text, and
 *   announcing it twice only slows a screen reader down. Pass `alt` when
 *   the avatar stands alone with no name next to it.
 */
import { useState } from 'react';

export const PersonAvatar = ({
  src,
  fallback,
  className = '',
  alt = '',
  title,
  loading = 'lazy'
}) => {
  // The src that failed, not a boolean. A boolean would stay true after the
  // picture changed — someone uploads a new one and still sees initials —
  // and fixing that from outside means every caller remembering a `key`.
  // Comparing against the current src resets itself.
  const [failedSrc, setFailedSrc] = useState(null);

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt={alt}
        aria-hidden={alt ? undefined : 'true'}
        title={title}
        loading={loading}
        onError={() => setFailedSrc(src)}
        className={`shrink-0 overflow-hidden rounded-full bg-gray-100 object-cover ${className}`}
      />
    );
  }

  return fallback;
};

export default PersonAvatar;
